import AnnouncementRoundedIcon from '@mui/icons-material/AnnouncementRounded'
import { Stack, Typography } from '@mui/material'
import React from 'react'
import { Controller } from 'react-hook-form'
import { ButtonSelectorOption } from '../../Common/buttonSelector/_props'
import { FormFieldFieldBaseProps } from '../../Common/form/formField/_props'
import UserFormField from '../UserFormField'
import { useFormContextUser } from '../utils/_utils'
import { useUserFormContext } from '../utils/userFormContext'

const SecuredEmail: React.FC<FormFieldFieldBaseProps> = ({ readonly }) => {
    const { control, watch } = useFormContextUser()
    const { state } = useUserFormContext()

    const validRpps = watch('validRpps')

    const mssEmails = state.annData?.mssEmails

    const options = React.useMemo<ButtonSelectorOption<string>[]>(() => {
        if (!mssEmails) return []

        return mssEmails.map<ButtonSelectorOption<string>>((mssEmail) => ({
            label: mssEmail,
            value: mssEmail,
        }))
    }, [mssEmails])

    const noSecureEmailText = 'Aucun courriel sécurisé'

    const sideTooltip = React.useMemo(
        () => (
            <Stack gap="1rem">
                <Stack direction="row" gap="1rem">
                    <AnnouncementRoundedIcon />
                    <Typography fontWeight={700}>Comment mettre à jour le courriel sécurisé ?</Typography>
                </Stack>
                <Stack>
                    <Typography>
                        Le courriel sécurisé est une donnée provenant de l’annuaire santé, à condition que votre compte
                        ait été créé depuis ce dernier.
                    </Typography>
                    <Typography>En fonction de votre situation, réalisez l’action suivante :</Typography>
                    <ul>
                        <Typography component="li">
                            Vous avez un ou plusieurs courriels sécurisés renseignés dans l’annuaire santé :
                            sélectionnez l’unique ou l’un des courriels parmi la liste, puis cliquer pour “enregistrer”
                            les modifications ;
                        </Typography>
                        <Typography component="li">
                            Vous n’avez ”aucun courriel sécurisé disponible” : cette information n’est pas modifiable,
                            contactez votre opérateur de messagerie sécurisée de santé pour que cette donnée soit mise à
                            jour dans l’annuaire santé, puis dans TokTokDoc.
                        </Typography>
                    </ul>
                </Stack>
            </Stack>
        ),
        [],
    )

    return (
        <Controller
            control={control}
            name="securedEmail"
            render={({ field, fieldState: { error } }) => {
                const hasNoOptions = options.length <= 1
                const fieldLength = field.value?.trim().length ?? 0
                const isValid = fieldLength > 0 || (fieldLength === 0 && validRpps)

                return (
                    <UserFormField
                        variant="select"
                        label="Courriel sécurisé"
                        type="default"
                        name={field.name}
                        locked={hasNoOptions}
                        status={{
                            error,
                            valid: isValid,
                        }}
                        readonly={readonly}
                        placeholder="Courriel sécurisé"
                        noValueText={validRpps ? noSecureEmailText : undefined}
                        value={field.value}
                        disabled={readonly || hasNoOptions}
                        sideTooltip={!readonly ? sideTooltip : null}
                        onChange={(e) => {
                            field.onChange(e)
                        }}
                        options={options}
                        dataCyScope="UserField"
                    />
                )
            }}
        />
    )
}

export default SecuredEmail
